import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Calendar, Newspaper, TrendingUp, Clock } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { ARTICLES, type Article } from "@/data/articles-loader";

const categoryColors: Record<Article["category"], string> = {
  IA: "bg-primary/10 text-primary border-primary/20",
  Negocios: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Economía: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  Tecnología: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  Marketing: "bg-pink-500/10 text-pink-700 border-pink-500/20",
};

const categories = ["Todas", "IA", "Negocios", "Economía", "Tecnología", "Marketing"] as const;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

const NoticiasPage = () => {
  const [activeCat, setActiveCat] = useState<(typeof categories)[number]>("Todas");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return ARTICLES.filter((n) => (activeCat === "Todas" ? true : n.category === activeCat))
      .filter((n) =>
        query.trim() === ""
          ? true
          : (n.title + " " + n.summary + " " + n.impact + " " + n.source)
              .toLowerCase()
              .includes(query.toLowerCase()),
      )
      .sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [activeCat, query]);

  const featured = filtered.find((n) => n.featured) ?? filtered[0];
  const rest = filtered.filter((n) => n !== featured);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang="es" />
        <title>Noticias de Marketing y Negocios Online | Vibra Marketing</title>
        <meta name="description" content="Análisis y noticias semanales de marketing digital, IA y negocios online para PYMEs en España. Por el equipo de Vibra Marketing." />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={`${SITE.url}/noticias`} />
        <link rel="alternate" hrefLang="es-ES" href={`${SITE.url}/noticias`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content="Noticias de Marketing y Negocios Online | Vibra Marketing" />
        <meta property="og:description" content="Análisis y noticias semanales de marketing digital, IA y negocios online para PYMEs en España." />
        <meta property="og:url" content={`${SITE.url}/noticias`} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbForPage([
          { name: "Inicio", path: "/" },
          { name: "Noticias", path: "/noticias" },
        ]))}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <Badge variant="secondary" className="mb-4 gap-1.5">
              <Newspaper className="w-3.5 h-3.5" />
              Noticias Vibra
            </Badge>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-[1.05] mb-5">
              Actualidad con visión de{" "}
              <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                negocio
              </span>
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl">
              Seleccionamos noticias de IA, economía y tecnología y te explicamos{" "}
              <span className="text-foreground font-medium">
                cómo afectan directamente a tu negocio en internet
              </span>
              . Sin ruido, solo lo que mueve la aguja.
            </p>
          </div>

          {/* Search */}
          <div className="mt-10 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar noticias…"
                className="w-full pl-11 pr-4 py-3 rounded-full bg-card border border-border focus:border-primary/50 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all text-sm"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCat(cat)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-all border",
                  activeCat === cat
                    ? "bg-foreground text-background border-foreground"
                    : "bg-card text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="relative py-8 md:py-12">
        <div className="container mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-24 text-muted-foreground">
              No hay noticias que coincidan con tu búsqueda.
            </div>
          ) : (
            <>
              {/* Featured article */}
              {featured && (
                <Link
                  to={`/noticias/${featured.slug}`}
                  className="group block mb-10 rounded-3xl border border-border bg-gradient-to-br from-primary/8 via-card to-purple-500/5 p-8 md:p-12 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/30 transition-all duration-500"
                >
                  <div className="flex items-center gap-3 mb-5 flex-wrap">
                    <Badge className="bg-foreground text-background hover:bg-foreground">
                      Destacado
                    </Badge>
                    <span
                      className={cn(
                        "px-2.5 py-1 rounded-full border text-[11px] font-medium",
                        categoryColors[featured.category],
                      )}
                    >
                      {featured.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {formatDate(featured.date)}
                    </span>
                    {featured.readingTime ? (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {featured.readingTime} min
                      </span>
                    ) : null}
                    <span className="text-xs text-muted-foreground">· {featured.source}</span>
                  </div>

                  <h2 className="font-display text-2xl md:text-4xl uppercase leading-tight mb-4 group-hover:text-primary transition-colors max-w-4xl">
                    {featured.title}
                  </h2>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6 max-w-3xl">
                    {featured.summary}
                  </p>

                  <div className="rounded-2xl bg-background/60 border border-primary/15 p-5 max-w-3xl">
                    <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                      <TrendingUp className="w-3.5 h-3.5" />
                      Impacto en tu negocio online
                    </div>
                    <p className="text-sm md:text-base leading-relaxed">{featured.impact}</p>
                  </div>

                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">
                    Leer análisis completo <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              )}

              {/* List of articles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {rest.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/noticias/${item.slug}`}
                    className="group flex flex-col rounded-2xl border border-border bg-card p-6 md:p-7 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span
                        className={cn(
                          "px-2.5 py-1 rounded-full border text-[11px] font-medium",
                          categoryColors[item.category],
                        )}
                      >
                        {item.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(item.date)}
                      </span>
                      {item.readingTime ? (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {item.readingTime} min
                        </span>
                      ) : null}
                      <span className="text-xs text-muted-foreground">· {item.source}</span>
                    </div>

                    <h3 className="font-semibold text-lg md:text-xl leading-snug mb-3 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {item.summary}
                    </p>

                    <div className="mt-auto rounded-xl bg-primary/5 border border-primary/10 p-4">
                      <div className="flex items-center gap-1.5 text-primary text-[11px] font-semibold uppercase tracking-wider mb-1.5">
                        <TrendingUp className="w-3 h-3" />
                        Impacto en tu negocio
                      </div>
                      <p className="text-xs md:text-sm leading-relaxed">{item.impact}</p>
                    </div>

                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">
                      Leer análisis <ArrowUpRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NoticiasPage;
